/**
 * Public events search logging for AdminHub Analytics.
 * Collection: eventSearchLogs — client create; admin list/delete.
 */

import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { shouldSkipAnalytics } from '../../analytics/analyticsActor';

export const EVENT_SEARCH_LOGS_COLLECTION = 'eventSearchLogs';

const QUERY_MAX = 120;

/**
 * @param {object} fields
 * @returns {object}
 */
export function buildEventSearchLogPayload(fields = {}) {
  const raw = String(fields.query || '').trim().slice(0, QUERY_MAX);
  const resultCount =
    typeof fields.resultCount === 'number' && Number.isFinite(fields.resultCount)
      ? Math.max(0, Math.floor(fields.resultCount))
      : 0;

  return {
    query: raw,
    queryLower: raw.toLowerCase(),
    resultCount,
    source: String(fields.source || 'events'),
    pathname: String(fields.pathname || '/'),
    uid: fields.uid ? String(fields.uid) : null,
    email: fields.email ? String(fields.email) : null,
    createdAt: fields.createdAt ?? serverTimestamp(),
  };
}

/**
 * Persist a search. Fire-and-forget from UI; failures should not break search.
 * @param {object} fields
 * @returns {Promise<string|null>}
 */
export async function logEventSearch(fields) {
  if (shouldSkipAnalytics()) return null;
  const payload = buildEventSearchLogPayload(fields);
  if (!payload.query || payload.query.length < 2) {
    return null;
  }
  const ref = await addDoc(collection(db, EVENT_SEARCH_LOGS_COLLECTION), payload);
  return ref.id;
}

/**
 * Admin list — newest first.
 * @param {{ limitCount?: number }} [opts]
 * @returns {Promise<object[]>}
 */
export async function listEventSearchLogs({ limitCount = 200 } = {}) {
  const q = query(
    collection(db, EVENT_SEARCH_LOGS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Aggregate top queries from a list of log rows (client-side).
 * @param {object[]} rows
 * @param {{ limitCount?: number }} [opts]
 * @returns {{ query: string, count: number, lastResultCount: number }[]}
 */
export function aggregateTopSearches(rows, { limitCount = 20 } = {}) {
  const byQuery = new Map();
  (rows || []).forEach((row) => {
    const key = String(row.queryLower || row.query || '')
      .trim()
      .toLowerCase();
    if (!key) return;
    const prev = byQuery.get(key) || {
      query: row.query || key,
      count: 0,
      lastResultCount: 0,
    };
    prev.count += 1;
    if (typeof row.resultCount === 'number') {
      prev.lastResultCount = row.resultCount;
    }
    byQuery.set(key, prev);
  });
  return Array.from(byQuery.values())
    .sort((a, b) => b.count - a.count || a.query.localeCompare(b.query))
    .slice(0, limitCount);
}
